import { FocusMonitor } from '@angular/cdk/a11y';
import {
  Component,
  ElementRef,
  EventEmitter,
  Host,
  HostBinding,
  Injector,
  Input,
  Output,
  Type,
  ViewChild,
  forwardRef
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteOrigin,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatFormField } from '@angular/material/form-field';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  AutoCompleteFormField,
  MAT_FORM_FIELD_CONTROL,
} from './autocomplete.formfield';
import { AutoCompleteConfig } from './config/autocomplete.config';
import { AutocompleteSearch } from './search/autocomplete.search';

const NG_VALUE_ACESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AutocompleteComponent),
  multi: true,
};

@Component({
  selector: 'tvk-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [NG_VALUE_ACESSOR, MAT_FORM_FIELD_CONTROL],
})
export class AutocompleteComponent
  extends AutoCompleteFormField
  implements ControlValueAccessor
{
  /**
   * Configuração de funcionamento do Autocomplete implementando o {AutoCompleteConfig}
   * <pre>
   * autocompleteAgente = new AutoCompleteConfig({\n
   *     service: this._infracaoService,\n
   *     keyword: 'dsInfracao',\n
   *     useLabel: 'dsInfracao',\n
   *     useValue: 'cdInfracao',\n
   *     labelFunction: (value: Infracao) => `${value.nrCodDetran} - ${value.dsInfracao}`\n
   *  })
   * </pre>
   *
   */
  @Input() config: AutoCompleteConfig;
  search: AutocompleteSearch;
  changes: Subject<any> = new Subject();
  results$: Promise<any>;

  @Input() uppercase: boolean = false;
  @Input() restrict: RegExp;

  connectedTo: MatAutocompleteOrigin;

  /**
   * Emite o valor escolhido ao selecionar no autocomplete
   */
  @Output() onChangeSelected: EventEmitter<any> = new EventEmitter();
  @Output() onChangeValue: EventEmitter<any> = new EventEmitter();

  @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocomplete, { static: true }) auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger, { static: true })
  trigger: MatAutocompleteTrigger;

  static nextId = 0;
  @HostBinding() id = `tvk-autocomplete-${AutocompleteComponent.nextId++}`;

  subscription: Subscription;

  onChange(value) {}
  onTouched() {}

  constructor(
    _focusMonitor: FocusMonitor,
    _elementRef: ElementRef,
    private _injector: Injector,
    @Host() private _matFormField: MatFormField
  ) {
    super(_focusMonitor, _elementRef);
  }

  ngAfterViewInit() {
    this.ngControl = this._injector.get<NgControl>(NgControl as unknown as Type<NgControl>);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
      this.ngControl.control.setValue(this.ngControl.control.value || undefined, { emitEvent: false });
    }

    this.search = new AutocompleteSearch(
      this.config || new AutoCompleteConfig()
    );

    this._defineElementMonitor(this.onTouched);
    this._defineConnected();

    this.changes
      .pipe(debounceTime(300))
      .subscribe(this._subscribeChanges.bind(this));
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }

  applyValue($event: MatAutocompleteSelectedEvent) {
    this.stateChanges.next();
    this.value = $event.option.value;
    this.onChangeSelected.emit(this.value);
    this.onChangeValue.emit(this.value);
    this.onChange(this.value[this.config.useValue]);
  }

  displayWith(value) {
    const label = this.config.labelFunction
      ? this.config.labelFunction(value)
      : value[this.config.useLabel];
    this.input.nativeElement.setSelectionRange(0, 0);
    return label;
  }

  writeValue(value: any): void {
    if (!this.input || !value) return this._clear();
    if (this.value && value !== this.value[this.config.useValue]) {
      this.value = null
    };
    if (this.value) return this._writeValue(this.value);

    this.search.get(value).then((result) => {
      this.value = result;
      this._writeValue(this.value);
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected _handleInput(event: KeyboardEvent) {
    const input = (event.currentTarget as HTMLInputElement);
    if(this.uppercase) input.value = input.value.toUpperCase();
    if(this.restrict) {
      input.value = input.value.replace(this.restrict, '');
    }

    return this.changes.next(event);
  }

  protected _handleFocusOut(event) {
    this.ngControl.control.markAllAsTouched();
  }

  private _subscribeChanges(event) {
    if (event.target.value.trim() === '') return this._clear();

    if(this.value === undefined) {
      this.value = null;
      this.onChange(null);
    };

    return (this.results$ = this.search.find(event.target.value));
  }

  private _defineConnected() {
    this.connectedTo = new MatAutocompleteOrigin(
      this._matFormField._connectionContainerRef
    );
  }

  private _writeValue(value) {
    this.input.nativeElement.value = value ? this.displayWith(value) : '';
    this.input.nativeElement.setSelectionRange(0, 0);

    this.onChangeValue.emit(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private _clear() {
    this.results$ = of([]).toPromise();
    this.value = undefined;
    this.onChange(undefined);
    this.input.nativeElement.value = '';
  }
}